/**
 * Timeline Bar Component
 * Implements PRD FR-32
 */

import React from 'react';
import { useSimulationStore } from '../state/simulationStore';
import { TimeStep } from '../model/types';

const TIME_DESCRIPTIONS: Record<TimeStep, { title: string; description: string }> = {
  1: {
    title: 'Date 1: Liquidity Event',
    description: 'LT1 arrives with liquidity need; trades with MMs',
  },
  2: {
    title: 'Date 2: Positions Unwind',
    description: 'LT2 arrives; all positions unwind to efficient price',
  },
  3: {
    title: 'Date 3: Terminal Value',
    description: 'Terminal value realized; no trading',
  },
};

export function TimelineBar() {
  const { simulation, jumpToTime } = useSimulationStore();

  if (!simulation) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="text-center text-gray-500">
          Run a simulation to see the timeline
        </div>
      </div>
    );
  }

  const currentTime = simulation.currentTime;

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between">
        {([1, 2, 3] as TimeStep[]).map((t, idx) => (
          <React.Fragment key={t}>
            {/* Timeline Node */}
            <button
              onClick={() => jumpToTime(t)}
              className={`flex flex-col items-center group relative ${
                t === currentTime ? 'scale-110' : ''
              }`}
            >
              {/* Circle */}
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                  t === currentTime
                    ? 'bg-blue-600 text-white shadow-lg'
                    : t < currentTime
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {t}
              </div>

              {/* Label */}
              <div className="mt-2 text-center">
                <div
                  className={`text-sm font-medium ${
                    t === currentTime ? 'text-blue-600' : 'text-gray-600'
                  }`}
                >
                  t = {t}
                </div>
              </div>

              {/* Tooltip */}
              <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                <div className="bg-gray-800 text-white text-xs rounded py-2 px-3 whitespace-nowrap">
                  <div className="font-semibold">{TIME_DESCRIPTIONS[t].title}</div>
                  <div className="text-gray-300 mt-1">
                    {TIME_DESCRIPTIONS[t].description}
                  </div>
                </div>
              </div>
            </button>

            {/* Connector Line */}
            {idx < 2 && (
              <div
                className={`flex-1 h-1 mx-2 rounded ${
                  t < currentTime ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Current Time Description */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <div className="font-semibold text-blue-800">
          {TIME_DESCRIPTIONS[currentTime].title}
        </div>
        <div className="text-sm text-blue-600 mt-1">
          {TIME_DESCRIPTIONS[currentTime].description}
        </div>
      </div>
    </div>
  );
}
